// 同一SKUに複数仕入先 — Stocky parity v2 Step 6。Pro 以上。
//
// 🔴 既定仕入先を切り替えると、サーバー側で `variants.supplier_id`（発注書ドラフト
// 生成が見ているミラー）も一緒に進む。だから既定を変えたら発注書ドラフトの
// キャッシュも捨てる必要がある（呼び出し側で ["purchase-orders"] を invalidate）。
import { apiFetch } from "./client";
import {
  canonicalStockyPublicBigIntId,
  type StockyPublicBigIntId,
} from "./stocky_public_ids";

export interface VariantSupplierLink {
  supplier_id: StockyPublicBigIntId;
  supplier_name: string | null;
  /** この仕入先の単価。null は未設定＝商品の原価（cost_override > cost > price）を使う。 */
  unit_cost: string | null;
  /** 入数（ケース単位）。null は未設定＝丸めない。 */
  pack_size: number | null;
  /** この仕入先の最低発注数量。null は未設定＝発注点設定の MOQ へフォールバック。 */
  min_order_qty: number | null;
  /** この仕入先のリードタイム。null は未設定＝発注点設定のリードタイムへ。 */
  lead_time_days: number | null;
  is_preferred: boolean;
}

export interface VariantSupplierConditions {
  unit_cost: string | null;
  pack_size: number | null;
  min_order_qty: number | null;
  lead_time_days: number | null;
}

export async function listVariantSuppliers(
  variantId: StockyPublicBigIntId,
): Promise<VariantSupplierLink[]> {
  const canonicalId = canonicalStockyPublicBigIntId(variantId);
  const links = await apiFetch<VariantSupplierLink[]>(
    `/api/variant-suppliers/${encodeURIComponent(canonicalId)}`,
  );
  return normalizeLinks(links);
}

function normalizeLinks(links: VariantSupplierLink[]): VariantSupplierLink[] {
  return links.map((link) => ({
    ...link,
    supplier_id: canonicalStockyPublicBigIntId(link.supplier_id),
  }));
}

export async function putVariantSupplier(
  variantId: StockyPublicBigIntId,
  supplierId: StockyPublicBigIntId,
  conditions: VariantSupplierConditions,
): Promise<VariantSupplierLink[]> {
  const links = await apiFetch<VariantSupplierLink[]>(
    `/api/variant-suppliers/${encodeURIComponent(canonicalStockyPublicBigIntId(variantId))}/${encodeURIComponent(canonicalStockyPublicBigIntId(supplierId))}`,
    { method: "PUT", body: conditions },
  );
  return normalizeLinks(links);
}

export async function deleteVariantSupplier(
  variantId: StockyPublicBigIntId,
  supplierId: StockyPublicBigIntId,
): Promise<VariantSupplierLink[]> {
  const links = await apiFetch<VariantSupplierLink[]>(
    `/api/variant-suppliers/${encodeURIComponent(canonicalStockyPublicBigIntId(variantId))}/${encodeURIComponent(canonicalStockyPublicBigIntId(supplierId))}`,
    { method: "DELETE" },
  );
  return normalizeLinks(links);
}

export async function setPreferredSupplier(
  variantId: StockyPublicBigIntId,
  supplierId: StockyPublicBigIntId | null,
): Promise<VariantSupplierLink[]> {
  const links = await apiFetch<VariantSupplierLink[]>(
    `/api/variant-suppliers/${encodeURIComponent(canonicalStockyPublicBigIntId(variantId))}/preferred`,
    {
      method: "POST",
      body: {
        supplier_id:
          supplierId === null
            ? null
            : canonicalStockyPublicBigIntId(supplierId),
      },
    },
  );
  return normalizeLinks(links);
}
